/**
 * AdminTaskDetailPage — the task workspace (TASK-UX1).
 *
 * One task at ``/admin/tasks/:taskId``, laid out as a wide main column
 * of things people READ and DISCUSS, and a narrow sidebar of things they
 * CLASSIFY and ATTACH:
 *
 *   main     Beschreibung · Details · Verknüpfungen · Zuweisung ·
 *            Rapporte · Notizen · Aktivitäten
 *   sidebar  Status · Tätigkeitsbereich · Zeiterfassung · Anhänge
 *
 * The page itself stays read-only; editing happens in the existing
 * ``TaskFormModal`` behind [Bearbeiten]. Save flow keeps its strict
 * order (T2.3.3), because closing before the refetch resolves shows the
 * pre-PATCH values for a few hundred milliseconds:
 *   submit → await PATCH → await refetchTask() → close → toast.
 *
 * Every number appears once. The booked effort lives in the sidebar's
 * Zeiterfassung card, so the reports section is asked to hide its own
 * KPI row (``showSummary={false}``) rather than print the same total a
 * second time half a screen away.
 *
 * Sections that look like they could merge, and why they do not:
 *   Beschreibung  what has to be done — set when the task is written
 *   Notizen       what people say about it as it runs (task_comments)
 *   Aktivitäten   what the SYSTEM recorded, nobody types into it
 *   Anhänge       files
 *
 * Mobile/tablet: single column, sidebar below, sticky action bar so
 * [Bearbeiten] stays reachable while scrolling.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  deleteAdminTask,
  fetchAdminTaskById,
  fetchAdminUsers,
  fetchAdminWorkReportById,
} from "../../api/adminData";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../contexts/AuthContext";
import useEditDetailLoader from "../../hooks/useEditDetailLoader";
import { useIsMobile } from "../../hooks/useIsMobile";
import WorkReportCancelDialog from "./calendar/workReports/WorkReportCancelDialog";
import WorkReportFormModal from "./calendar/workReports/WorkReportFormModal";
import TaskActivitySection from "./tasks/TaskActivitySection";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import TaskAttachmentsSection from "./tasks/TaskAttachmentsSection";
import TaskCategoryCard from "./tasks/TaskCategoryCard";
import TaskCommentsSection from "./tasks/TaskCommentsSection";
import TaskTimeCard from "./tasks/TaskTimeCard";
import TaskWorkReportsSection from "./tasks/TaskWorkReportsSection";
// ASSIGN1: the SAME name rule the picker uses — label, else email, never
// the id. Two spellings of a colleague's name across two surfaces of one
// task would read as two different people.
import { userName } from "./users/UserPicker";
import { useTaskWorkReports } from "./tasks/useTaskWorkReports";
import EntityLinkGroups from "./entityLinks/EntityLinkGroups";
import TaskFormModal, {
  PRIORITY_OPTIONS,
  TASK_TYPE_OPTIONS,
} from "./tasks/TaskFormModal";
import TaskStatusActions from "./tasks/TaskStatusActions";
import {
  EmptyPlaceholder,
  MetaRow,
  PriorityPill,
  STATUS_OPTIONS,
  Section,
  StatusPill,
  TaskTypePill,
  personInitials,
  renderTermineBlock,
} from "./tasks/taskDetailPrimitives";
import { formatDateTime } from "../../utils/datetime";


const TASK_TYPE_LABEL = Object.fromEntries(
  TASK_TYPE_OPTIONS.map((o) => [o.value, o.label])
);
const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
);
const PRIORITY_LABEL = Object.fromEntries(
  PRIORITY_OPTIONS.map((o) => [o.value, o.label])
);


export default function AdminTaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // T2.4.2: AuthContext gives us the currently-logged-in user so
  // TaskCommentsSection can decide whose notes show edit/delete
  // buttons. May be null/undefined briefly while session is restored —
  // the notes component renders no action buttons in that case.
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || null;

  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  // C1.1: "Rapport erfassen" opens the shared work-report form with this
  // task pre-linked, so time booked on a task is one click away instead
  // of a detour via the calendar plus a search for the task by title.
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // D1: the SAME modal serves create and edit — `editingReport` decides
  // which. A second form would be a second place for the payload shape
  // to drift.
  const [editingReport, setEditingReport] = useState(null);
  const [pendingReportDelete, setPendingReportDelete] = useState(null);
  // T2.4.1: bumped whenever a sibling section creates an audit event
  // (e.g. a new note or an uploaded file). TaskActivitySection re-fetches
  // when this changes, so the new audit row surfaces without a reload.
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // D1: reports + all-time effort for THIS task. Owns its own loading
  // and error state so a failing report list never blanks the task.
  const taskWorkReports = useTaskWorkReports(taskId);

  const usersById = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [users]);

  /**
   * C1.1/C3 — links the new report inherits from this task. Each entity
   * is carried over only when the task actually has it; otherwise the key
   * stays empty and that picker behaves like anywhere else (it loads its
   * normal first page). Memoised on the ids so a task refetch does not
   * hand the modal a new object for no reason.
   */
  const reportDefaults = useMemo(
    () => ({
      task_id: taskId || "",
      tenancy_id: task?.tenancy_id ? String(task.tenancy_id) : "",
      tenant_id: task?.tenant_id ? String(task.tenant_id) : "",
      application_id: task?.application_id ? String(task.application_id) : "",
      unit_id: task?.unit_id ? String(task.unit_id) : "",
      landlord_id: task?.landlord_id ? String(task.landlord_id) : "",
    }),
    [
      taskId,
      task?.tenancy_id,
      task?.tenant_id,
      task?.application_id,
      task?.unit_id,
      task?.landlord_id,
    ]
  );

  const refetchTask = useCallback(() => {
    if (!taskId) return Promise.resolve();
    return fetchAdminTaskById(taskId)
      .then((data) => {
        setTask(data);
        setError("");
      })
      .catch((e) => {
        const msg = e?.message || "Aufgabe konnte nicht geladen werden.";
        setError(msg);
        setTask(null);
        toast.error(msg);
      });
  }, [taskId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      refetchTask(),
      fetchAdminUsers()
        .then((rows) => setUsers(Array.isArray(rows) ? rows : []))
        .catch(() => setUsers([])),
    ]).finally(() => setLoading(false));
  }, [refetchTask]);

  /**
   * STRICT ordering per the T2.3.3 brief: refetch first, then close
   * the modal, then surface the success toast. Closing first would
   * leave the page showing pre-PATCH data for a few hundred ms while
   * the GET resolves — a visible stale-data flash.
   */
  const handleSaved = useCallback(async () => {
    try {
      await refetchTask();
      setModalOpen(false);
    } catch {
      // refetchTask already toasted; leave modal open so the user can
      // re-submit or close manually.
    }
  }, [refetchTask]);

  // D1 — one modal, two entry points. Create clears any edit target so
  // a previous "Bearbeiten" cannot leak into the next "Rapport erfassen".
  const handleReportCreate = useCallback(() => {
    setEditingReport(null);
    setReportModalOpen(true);
  }, []);

  // PICK1a: the rows in this section are list shapes and carry no link
  // ARRAYS — only the legacy singular ids. Editing one straight from the
  // row and saving would write back at most one link per type and delete
  // the rest, so the row supplies an id and the form gets the full
  // WorkReportDetailRead.
  const { requestEdit: handleReportEdit } = useEditDetailLoader({
    fetchDetail: fetchAdminWorkReportById,
    onLoaded: useCallback((detail) => {
      setEditingReport(detail);
      setReportModalOpen(true);
    }, []),
    errorMessage: "Der Rapport konnte nicht geladen werden.",
  });

  /** After create or edit: reload BOTH the rows and the server-side
   * total, and refresh the activity feed (the mutation writes an audit
   * row). The task itself is deliberately NOT refetched — booking time
   * never changes a task's status or fields. */
  const handleReportSaved = useCallback(() => {
    void taskWorkReports.refresh();
    setActivityRefreshKey((k) => k + 1);
  }, [taskWorkReports]);

  /** An upload or a delete writes an audit row on the task, so the
   * activity sections need to know — the task itself does not change. */
  const handleAttachmentsChanged = useCallback(() => {
    setActivityRefreshKey((k) => k + 1);
  }, []);

  // SAFE-DELETE1: the full task delete. `deleteBlocked` holds the
  // backend's 409 message (work-report count included) so the refusal
  // gets its own dialog instead of a vanishing toast.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(null);

  const handleConfirmedDelete = useCallback(async () => {
    if (!taskId || deleting) return;
    setDeleting(true);
    try {
      await deleteAdminTask(taskId);
      toast.success("Aufgabe gelöscht.");
      navigate("/admin/tasks");
    } catch (err) {
      setConfirmDeleteOpen(false);
      if (err?.status === 409) {
        // Blocked by linked reports — nothing was touched. The message
        // names the count; the page stays where it is.
        setDeleteBlocked(
          err.message ||
            "Diese Aufgabe kann nicht gelöscht werden, weil Rapporte mit ihr verknüpft sind."
        );
      } else {
        toast.error(err?.message || "Aufgabe konnte nicht gelöscht werden.");
      }
    } finally {
      setDeleting(false);
    }
  }, [taskId, deleting, navigate]);

  function userDisplay(userId) {
    if (!userId) return "—";
    const u = usersById[userId];
    if (!u) return userId; // raw id fallback (brief-accepted in T2.3.3)
    return u.full_name || u.email || userId;
  }

  // ── Header (rendered eagerly so loading / error states share it) ──
  const breadcrumb = (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 text-[11px] text-muted-foreground dark:text-[#4a5070]"
    >
      <Link
        to="/admin/calendar"
        className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:text-[#94a3b8]"
      >
        Kalender
      </Link>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <Link
        to="/admin/tasks"
        className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:text-[#94a3b8]"
      >
        Aufgaben
      </Link>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <span aria-current="page" className="text-foreground dark:text-[#94a3b8]">
        {task?.title || "…"}
      </span>
    </nav>
  );

  if (loading && !task) {
    return (
      <div className="mx-auto max-w-[1400px]">
        {breadcrumb}
        <p
          role="status"
          className="text-[13px] text-muted-foreground dark:text-[#4a5070]"
        >
          Lade Aufgabe …
        </p>
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className="mx-auto max-w-[1400px]">
        {breadcrumb}
        <div
          role="alert"
          className="rounded-[10px] border border-dashed border-destructive/40 bg-destructive/10 p-6 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
        >
          <p className="text-[13px] font-medium text-destructive dark:text-[#ff6b6b]">
            {error}
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-3 rounded-[8px] border border-border bg-transparent px-4 py-2 text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  if (!task) return null;

  const assigneeName = task.assigned_to_user_id
    ? userDisplay(task.assigned_to_user_id)
    : null;

  // ASSIGN1: the detail response ships these already named, so nothing
  // here resolves an id — which is why a deactivated colleague still
  // reads as a person rather than as a blank.
  const participants = Array.isArray(task.participants)
    ? task.participants
    : [];

  // ── Action bar (sticky on mobile so it stays reachable) ──
  const actions = (
    <div
      className={
        isMobile
          ? "sticky bottom-0 z-10 -mx-3 flex items-center justify-end gap-2 border-t border-border bg-background/95 px-3 py-3 backdrop-blur dark:border-[#1e2130] dark:bg-[#0d0f14]/95"
          : "flex items-center gap-2"
      }
    >
      {/* SAFE-DELETE1 — full deletion, guarded by the backend's 409
          when any report references the task. */}
      <button
        type="button"
        onClick={() => setConfirmDeleteOpen(true)}
        className="min-h-[44px] rounded-[8px] border border-destructive/40 bg-transparent px-4 py-2 text-[13px] font-semibold text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Aufgabe löschen
      </button>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="min-h-[44px] rounded-[8px] border border-border bg-transparent px-4 py-2 text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
      >
        Zurück
      </button>
      <button
        type="button"
        onClick={handleReportCreate}
        className="min-h-[44px] rounded-[8px] border border-border bg-transparent px-4 py-2 text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
      >
        Rapport erfassen
      </button>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="min-h-[44px] rounded-[8px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
      >
        Bearbeiten
      </button>
    </div>
  );

  const headerBlock = (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <h1 className="m-0 break-words text-[22px] font-bold tracking-tight text-foreground dark:text-[#e2e4ed]">
          {task.title}
        </h1>
        <div className="mt-2 flex flex-wrap gap-2">
          <StatusPill value={task.status} labels={STATUS_LABEL} />
          <PriorityPill value={task.priority} labels={PRIORITY_LABEL} />
          <TaskTypePill value={task.task_type} labels={TASK_TYPE_LABEL} />
        </div>
      </div>
      {!isMobile ? actions : null}
    </header>
  );

  // ── Main column ──
  const main = (
    <div className="grid gap-4">
      <Section title="Beschreibung">
        {task.description ? (
          <p className="m-0 whitespace-pre-wrap text-[13px] text-foreground dark:text-[#c5cbe0]">
            {task.description}
          </p>
        ) : (
          <EmptyPlaceholder>Keine Beschreibung erfasst.</EmptyPlaceholder>
        )}
      </Section>

      {/* Details — the task's own facts. Termine keeps the calendar's
          shape classifier; the rows below are provenance, which belongs
          next to the dates rather than in a card of its own. */}
      <Section title="Details">
        <div className="mb-2">{renderTermineBlock(task)}</div>
        <dl className="grid grid-cols-1 gap-0">
          <MetaRow
            label="Erstellt von"
            value={userDisplay(task.created_by_user_id)}
          />
          <MetaRow label="Erstellt am" value={formatDateTime(task.created_at)} />
          <MetaRow
            label="Aktualisiert am"
            value={task.updated_at ? formatDateTime(task.updated_at) : null}
          />
        </dl>
      </Section>

      {/* ME3a: grouped multi-links. Replaces the D4c1 one-row-per-type
          list, which could only ever show a single link because that is
          all the legacy columns held. Same visual language, several
          values per type, and each one says whether a person chose it or
          the server derived it. The singular fields are still on the
          response and deliberately NOT rendered a second time — one
          number, one place. */}
      <Section title="Verknüpfungen">
        <EntityLinkGroups row={task} />
      </Section>

      {/* ASSIGN1: one responsible person, then whoever else is helping.
          The reference layout shows an avatar here; it is initials on a
          token surface, never a photo we do not store.

          The two are rendered as distinct roles rather than one people
          list, because that is what they are — the label under the first
          name is the only thing telling a reader which of them owns the
          task. Participants carry no label of their own beyond the
          heading; repeating "Beteiligt" under every one of them would be
          noise. */}
      <Section title="Zuweisung">
        {assigneeName ? (
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[12px] font-bold uppercase text-muted-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#94a3b8]"
            >
              {personInitials(assigneeName)}
            </span>
            <div className="min-w-0">
              <p className="m-0 text-[13px] font-semibold text-foreground dark:text-[#c5cbe0]">
                {assigneeName}
              </p>
              <p className="m-0 text-[11px] text-muted-foreground dark:text-[#4a5070]">
                Hauptverantwortlich
              </p>
            </div>
          </div>
        ) : (
          <EmptyPlaceholder>Niemand zugewiesen.</EmptyPlaceholder>
        )}

        <div className="mt-4 border-t border-border pt-3 dark:border-[#1e2130]">
          <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground dark:text-[#4a5070]">
            Beteiligt
          </p>
          {participants.length === 0 ? (
            <p className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]">
              Keine weiteren Beteiligten
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {participants.map((p) => {
                const name = userName(p);
                return (
                  <li key={p.id} className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[10px] font-bold uppercase text-muted-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#94a3b8]"
                    >
                      {personInitials(name)}
                    </span>
                    <div className="min-w-0">
                      <p className="m-0 truncate text-[13px] text-foreground dark:text-[#c5cbe0]">
                        {name}
                        {p.is_active === false ? (
                          <span className="ml-2 text-[11px] text-muted-foreground dark:text-[#4a5070]">
                            Inaktiv
                          </span>
                        ) : null}
                      </p>
                      {p.email && p.email !== name ? (
                        <p className="m-0 truncate text-[11px] text-muted-foreground dark:text-[#4a5070]">
                          {p.email}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Section>

      {/* D1 — placed after the task's own facts and BEFORE the
          discussion/audit sections: booked effort is task data, notes
          and activity are commentary on it. It brings its own Section
          chrome. TASK-UX1 hides its KPI row: the same aggregate is in
          the sidebar's Zeiterfassung card, and printing a total twice
          on one screen invites the two to disagree. */}
      <TaskWorkReportsSection
        workReports={taskWorkReports}
        showSummary={false}
        onCreateClick={handleReportCreate}
        onEditClick={handleReportEdit}
        onDeleteClick={setPendingReportDelete}
      />

      {/* TASK-NOTES1 — internal notes, backed by the existing
          ``task_comments`` table and its full CRUD. Renamed, not
          rebuilt: a second table with the same columns would have split
          the history of every task that already has entries. */}
      <Section title="Notizen">
        <TaskCommentsSection
          taskId={taskId}
          currentUserId={currentUserId}
          onCommentChanged={() => setActivityRefreshKey((k) => k + 1)}
        />
      </Section>

      {/* D3: the unified feed is the primary activity view — it merges
          the task's own events with its notes and the reports booked
          on it. The narrower audit list stays below under its technical
          name rather than being deleted: it still shows actions the
          feed's curated allowlist leaves out (note edits/deletes), so
          removing it would lose function. */}
      <EntityActivityFeed
        entityType="task"
        entityId={taskId}
        headingId="task-activity-heading"
      />

      <Section title="Änderungsprotokoll">
        <TaskActivitySection
          taskId={taskId}
          refreshKey={activityRefreshKey}
        />
      </Section>
    </div>
  );

  // ── Sidebar (right column on lg+, stacked below on smaller screens) ──
  // Sticky from lg upwards so the classification cards stay beside the
  // (much taller) notes/activity column instead of floating alone next
  // to empty space once the user scrolls.
  const sidebar = (
    <aside className="grid gap-4 lg:sticky lg:top-4 lg:self-start">
      {/* Status — the workflow buttons plus, once it is finished, who
          finished it and when. The current status itself is the pill in
          the page header; repeating it here would be the same fact in
          two places. TaskStatusActions returns null for a cancelled
          task, so the card can end up holding only the closing facts. */}
      <Section title="Status">
        <TaskStatusActions
          task={task}
          variant="detail"
          onChanged={() => void refetchTask()}
        />
        {task.status === "done" ? (
          <dl className="mt-3 grid grid-cols-1 gap-0">
            <MetaRow
              label="Abgeschlossen am"
              value={formatDateTime(task.completed_at)}
            />
            <MetaRow
              label="Abgeschlossen von"
              value={userDisplay(task.completed_by_user_id)}
            />
          </dl>
        ) : null}
      </Section>

      <TaskCategoryCard
        code={task.activity_category}
        headingId="task-category-heading"
      />

      <TaskTimeCard
        workReports={taskWorkReports}
        headingId="task-time-heading"
      />

      <TaskAttachmentsSection
        taskId={taskId}
        onChanged={handleAttachmentsChanged}
      />
    </aside>
  );

  return (
    <div className="mx-auto max-w-[1400px] pb-20">
      {breadcrumb}
      {headerBlock}

      {/* Roughly 72/28 on large screens, matching the reference layout:
          the main column has to hold a table and a discussion, the
          sidebar only ever holds cards. Stacked below lg, sidebar last,
          which is the M1/M2 convention the tasks list already uses. */}
      <div className="grid gap-4 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px]">
        <div className="min-w-0">{main}</div>
        <div className="min-w-0">{sidebar}</div>
      </div>

      {isMobile ? actions : null}

      <TaskFormModal
        open={modalOpen}
        mode="edit"
        task={task}
        onOpenChange={setModalOpen}
        onSaved={handleSaved}
      />

      {/* C1.1 — same shared form as the calendar uses, opened in create
          mode with this task (and its tenancy, if any) pre-linked. */}
      <WorkReportFormModal
        open={reportModalOpen}
        mode={editingReport ? "edit" : "create"}
        report={editingReport}
        currentUserId={currentUserId}
        createDefaults={reportDefaults}
        onOpenChange={(next) => {
          setReportModalOpen(next);
          // Drop the edit target on close so the next open starts clean.
          if (!next) setEditingReport(null);
        }}
        onSaved={handleReportSaved}
      />

      {/* D1 — the SAME confirmation the calendar's list view uses, so
          there is one delete path for reports in the app. */}
      <WorkReportCancelDialog
        report={pendingReportDelete}
        onCancel={() => setPendingReportDelete(null)}
        onCancelled={() => {
          setPendingReportDelete(null);
          handleReportSaved();
        }}
      />

      {/* SAFE-DELETE1 — confirm first (GO wording), then the backend
          decides. */}
      <AlertDialog
        open={confirmDeleteOpen}
        onOpenChange={(next) => {
          if (!deleting) setConfirmDeleteOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Aufgabe, ihre Notizen, Verknüpfungen und Anhänge werden
              dauerhaft entfernt. Diese Aktion kann nicht rückgängig
              gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmedDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Löscht …" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* The 409 refusal — its own dialog, because a toast with the
          count would vanish before it is read. Nothing was deleted and
          no report is ever decoupled automatically. */}
      <AlertDialog
        open={deleteBlocked != null}
        onOpenChange={(next) => {
          if (!next) setDeleteBlocked(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Aufgabe kann nicht gelöscht werden
            </AlertDialogTitle>
            <AlertDialogDescription>{deleteBlocked}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDeleteBlocked(null)}>
              Verstanden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
